import { Col, Row } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineAlternateEmail,
  MdOutlineCall,
  MdOutlineCheckCircle,
  MdOutlineFlaky,
  MdOutlinePinDrop,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM, SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Contact = ({ setPercent, isFieldRequired, initialValues, bindField }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const zipCodePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const zipCode = get(initialValues, zipCodePath);
  const { cities, loading: citiesLoading } = useZipCodeCities(zipCode);

  const addressPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS}.value`;
  const addressField = bindField(addressPath, {
    label: SUBSCRIPTION.FORM.ADDRESS.LABEL,
    required: isFieldRequired(addressPath),
    onBlur: () => setPercent(initialValues),
  });

  const streetPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.STREET}.value`;
  const streetField = bindField(streetPath, {
    label: SUBSCRIPTION.FORM.STREET.LABEL,
    required: isFieldRequired(streetPath),
    onBlur: () => setPercent(initialValues),
  });

  const complementPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`;
  const complementField = bindField(complementPath, {
    label: SUBSCRIPTION.FORM.COMPLEMENT.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const zipCodeField = bindField(zipCodePath, {
    label: SUBSCRIPTION.FORM.POSTAL_CODE.LABEL,
    required: isFieldRequired(zipCodePath),
    disabled: true,
  });

  const cityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const cityField = bindField(cityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(cityPath),
    disabled: true,
    onBlur: () => setPercent(initialValues),
  });

  const phonePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`;
  const phoneField = bindField(phonePath, {
    label: SUBSCRIPTION.FORM.MOBILE.LABEL,
    required: isFieldRequired(phonePath),
    onBlur: () => setPercent(initialValues),
  });

  const landlinePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.LANDLINE}.value`;
  const landlineField = bindField(landlinePath, {
    label: SUBSCRIPTION.FORM.LANDLINE.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const teletransmissionPath = "project.contact.fields.teletransmission";
  const teletransmissionField = bindField(teletransmissionPath, {
    label: SUBSCRIPTION.FORM.TELETRANSMISSION.LABEL,
    onChange: () => setPercent(initialValues),
  });

  const emailPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`;
  const emailField = bindField(emailPath, {
    label: SUBSCRIPTION.FORM.EMAIL.LABEL,
    required: isFieldRequired(emailPath),
    onBlur: () => setPercent(initialValues),
  });

  const acceptContactPath = "project.contact.acceptContact";
  const acceptContactField = bindField(acceptContactPath, {
    label: SUBSCRIPTION.FORM.CONFIRM_CONTACT.LABEL,
    onChange: () => setPercent(initialValues),
  });

  const acceptDigitalPath = "project.contact.acceptDigital";
  const acceptDigitalField = bindField(acceptDigitalPath, {
    label: SUBSCRIPTION.FORM.DIGITAL_OPTION.LABEL,
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.ADDRESS.TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={12} xs={12}>
                  <FormField
                    path={addressField.path}
                    label={addressField.label}
                    required={addressField.required}
                    disabled={addressField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={addressField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.ADDRESS.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={addressField.defaultValue}
                        {...field}
                        onChange={addressField.onChange}
                        onBlur={addressField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={12} xs={12}>
                  <FormField
                    path={streetField.path}
                    label={streetField.label}
                    required={streetField.required}
                    disabled={streetField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={streetField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.STREET.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={streetField.defaultValue}
                        {...field}
                        onChange={streetField.onChange}
                        onBlur={streetField.onBlur}
                        optional={!streetField.required}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={complementField.path}
                    label={complementField.label}
                    required={complementField.required}
                    disabled={complementField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={complementField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.COMPLEMENT.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={complementField.defaultValue}
                        {...field}
                        onChange={complementField.onChange}
                        onBlur={complementField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={zipCodeField.path}
                    label={zipCodeField.label}
                    required={zipCodeField.required}
                    disabled={zipCodeField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={zipCodeField.label}
                        icon={<MdOutlinePinDrop size={16} />}
                        placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                        defaultValue={zipCode}
                        {...field}
                        onChange={zipCodeField.onChange}
                        onBlur={zipCodeField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={cityField.path}
                    label={cityField.label}
                    required={cityField.required}
                    disabled={cityField.disabled}
                  >
                    {(field) => (
                      <Select
                        icon={<MdOutlineSouthAmerica size={16} />}
                        label={cityField.label}
                        placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                        theme="light"
                        showSearch
                        loading={citiesLoading}
                        options={cities}
                        defaultValue={cityField.defaultValue}
                        {...field}
                        onChange={cityField.onChange}
                        onBlur={cityField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.TITLE
              }
              subtitle={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.SUBTITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <Alert
                    showIcon
                    type="info"
                    message={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE
                        .INFO
                    }
                  />
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={phoneField.path}
                    label={phoneField.label}
                    required={phoneField.required}
                    disabled={phoneField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={phoneField.label}
                        icon={<MdOutlineCall size={16} />}
                        placeholder={SUBSCRIPTION.FORM.MOBILE.PLACEHOLDER}
                        type="custom"
                        stripSpaces={true}
                        defaultValue={phoneField.defaultValue}
                        {...field}
                        onChange={phoneField.onChange}
                        onBlur={phoneField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={landlineField.path}
                    label={landlineField.label}
                    required={landlineField.required}
                    disabled={landlineField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={landlineField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineCall size={16} />}
                        placeholder={SUBSCRIPTION.FORM.LANDLINE.PLACEHOLDER}
                        type="custom"
                        trim={true}
                        defaultValue={landlineField.defaultValue}
                        {...field}
                        onChange={landlineField.onChange}
                        onBlur={landlineField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={teletransmissionField.path}
              label={teletransmissionField.label}
              required={teletransmissionField.required}
              disabled={teletransmissionField.disabled}
            >
              {(field) => (
                <Input
                  type="switch"
                  label={teletransmissionField.label}
                  icon={<MdOutlineCheckCircle size={16} />}
                  defaultChecked={get(initialValues, teletransmissionPath)}
                  {...field}
                  onChange={teletransmissionField.onChange}
                  onBlur={teletransmissionField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={emailField.path}
              label={emailField.label}
              required={emailField.required}
              disabled={emailField.disabled}
            >
              {(field) => (
                <Input
                  label={emailField.label}
                  icon={<MdOutlineAlternateEmail size={16} />}
                  placeholder={SUBSCRIPTION.FORM.EMAIL.PLACEHOLDER}
                  defaultValue={emailField.defaultValue}
                  {...field}
                  onChange={emailField.onChange}
                  onBlur={emailField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={acceptContactField.path}
              label={acceptContactField.label}
              required={acceptContactField.required}
              disabled={acceptContactField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdOutlineFlaky size={16} />}
                  label={acceptContactField.label}
                  values={[
                    {
                      value: true,
                      label: SUBSCRIPTION.FORM.CONFIRM_CONTACT.OPTIONS.YES,
                    },
                    {
                      value: false,
                      label: SUBSCRIPTION.FORM.CONFIRM_CONTACT.OPTIONS.NO,
                    },
                  ]}
                  value={get(initialValues, acceptContactPath)}
                  {...field}
                  onChange={acceptContactField.onChange}
                  onBlur={acceptContactField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <Alert
              showIcon
              type="info"
              message={SUBSCRIPTION.FORM.CONFIRM_CONTACT.INFO_AMELLIS}
            />
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={acceptDigitalField.path}
              label={acceptDigitalField.label}
              required={acceptDigitalField.required}
              disabled={acceptDigitalField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdOutlineFlaky size={16} />}
                  label={acceptDigitalField.label}
                  values={[
                    {
                      value: true,
                      label: SUBSCRIPTION.FORM.DIGITAL_OPTION.OPTIONS.YES,
                    },
                    {
                      value: false,
                      label: SUBSCRIPTION.FORM.DIGITAL_OPTION.OPTIONS.NO,
                    },
                  ]}
                  value={get(initialValues, acceptDigitalPath)}
                  {...field}
                  onChange={acceptDigitalField.onChange}
                  onBlur={acceptDigitalField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <Alert
              showIcon
              type="info"
              message={SUBSCRIPTION.FORM.DIGITAL_OPTION.INFO}
            />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Contact;
